import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { Crown, Medal, TrendingUp } from "lucide-react";

export default function Leaderboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard").then((r) => {
      setRows(r.data);
      setLoading(false);
    });
  }, []);

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  const podiumColors = ["#FFB703", "#C0C0C0", "#CD7F32"];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="leaderboard-page">
      <div className="text-[11px] uppercase tracking-[0.3em] font-mono font-bold text-[#00D37F]">
        {t("nav_leaderboard")} · {t("standings").toUpperCase()}
      </div>
      <h1 className="font-display text-5xl sm:text-6xl mt-2 mb-10">
        {t("live_board").split(" ")[0]} <span className="text-[#00D37F]">{t("live_board").split(" ").slice(1).join(" ")}</span>
      </h1>

      {loading ? (
        <div className="text-white/50">…</div>
      ) : rows.length === 0 ? (
        <div className="text-white/50 text-sm font-mono">{t("no_players").toUpperCase()}</div>
      ) : (
        <>
          {/* Podium */}
          {top3.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="podium">
              {top3.map((row, i) => (
                <div
                  key={row.id}
                  className="scoreboard relative p-6"
                  style={{ borderColor: `${podiumColors[i]}66` }}
                  data-testid={`podium-${i + 1}`}
                >
                  <div className="absolute top-0 left-0 right-0 h-1" style={{ background: podiumColors[i] }} />
                  <div className="flex items-start justify-between">
                    <div
                      className="font-display text-6xl leading-none"
                      style={{ color: podiumColors[i] }}
                    >
                      #{row.rank}
                    </div>
                    {i === 0 ? (
                      <Crown className="w-6 h-6" style={{ color: podiumColors[i] }} />
                    ) : (
                      <Medal className="w-6 h-6" style={{ color: podiumColors[i] }} />
                    )}
                  </div>
                  <div className="mt-6">
                    <div className="font-head text-xl">@{row.username}</div>
                    <div className="text-xs text-white/50 font-mono mt-1">{row.name}</div>
                  </div>
                  <div className="mt-5 pt-5 border-t border-white/5 flex items-end justify-between">
                    <span className="text-[10px] uppercase tracking-[0.25em] font-mono font-bold text-white/40">
                      {t("points_label")}
                    </span>
                    <span
                      className="font-mono text-4xl font-bold tabular-nums"
                      style={{ color: podiumColors[i] }}
                    >
                      {row.points ?? 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Rest */}
          {rest.length > 0 && (
            <div className="border border-white/10 bg-[#0d0d0d]" data-testid="leaderboard-table">
              <div className="grid grid-cols-[60px_1fr_140px] items-center border-b border-white/10 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em] font-bold text-white/50">
                <span>{t("rank")}</span>
                <span>{t("player")}</span>
                <span className="text-right flex items-center justify-end gap-1">
                  <TrendingUp className="w-3 h-3" /> {t("points")}
                </span>
              </div>
              {rest.map((row) => {
                const isMe = user && row.id === user.id;
                return (
                  <div
                    key={row.id}
                    className={`grid grid-cols-[60px_1fr_140px] items-center px-4 py-4 border-b border-white/5 transition-colors ${
                      isMe ? "bg-[#00D37F]/10" : "hover:bg-white/[0.03]"
                    }`}
                    data-testid={`leader-row-${row.id}`}
                  >
                    <span className="font-display text-2xl leading-none">#{row.rank}</span>
                    <span className="flex items-center gap-3">
                      <span className="w-9 h-9 bg-[#00D37F]/15 border border-[#00D37F]/30 flex items-center justify-center text-xs font-head text-[#00D37F]">
                        {row.username?.[0]?.toUpperCase()}
                      </span>
                      <span>
                        <span className="block font-head text-sm">@{row.username}</span>
                        <span className="block text-[11px] text-white/40 font-mono">{row.name}</span>
                      </span>
                      {isMe && (
                        <span className="text-[9px] uppercase tracking-[0.25em] font-mono font-bold text-[#00D37F] bg-[#00D37F]/10 border border-[#00D37F]/30 px-2 py-0.5">
                          {t("you_chip")}
                        </span>
                      )}
                    </span>
                    <span className="font-mono text-2xl font-bold text-right tabular-nums text-[#00D37F]">
                      {row.points ?? 0}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
